/**
 * ProductCard — the storefront product tile.
 *
 * Works with live API products. Shows the real photo when one exists, otherwise
 * a friendly emoji stand-in (image hosting comes later). Tapping the tile or
 * name opens the product page; the Add button is separate so it never navigates.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { formatCurrency, calculateDiscountPercent, productSizeLabel } from '@rocket07/shared';
import { ROUTES } from '@/constants/routes';
import { productImage } from '@/utils/productImage';
import { AddButton } from './AddButton';
import { AiImageNote } from './AiImageNote';

export function ProductCard({ product }) {
  const discount = calculateDiscountPercent(product.mrp, product.price);
  const hasDiscount = discount > 0;
  const to = ROUTES.product(product.slug);
  const sizeLabel = productSizeLabel(product);

  // Prefer an explicit API image, else the name→photo map. If the file isn't
  // there yet, onError drops us back to the emoji so nothing looks broken.
  //
  // Remembers WHICH url failed, not just THAT one did: a plain boolean latches,
  // so a card whose old photo 404'd would keep showing the emoji even after an
  // admin uploaded a working one and the list refetched into the same instance.
  const [failedSrc, setFailedSrc] = useState(null);
  const candidate = product.image || productImage(product.name);
  const imageSrc = candidate && candidate !== failedSrc ? candidate : null;

  return (
    <div className="flex flex-col">
      {/* Image tile */}
      <Link
        to={to}
        className="relative mb-1 grid aspect-square place-items-center overflow-hidden rounded-xl bg-tile"
      >
        {imageSrc ? (
          <img
            src={imageSrc}
            alt={product.name}
            loading="lazy"
            onError={() => setFailedSrc(imageSrc)}
            className="h-full w-full object-contain p-2"
          />
        ) : (
          <span className="text-5xl" aria-hidden="true">
            {product.emoji}
          </span>
        )}
      </Link>

      {/*
        Only when a real photo rendered — the emoji fallback is not generated
        art, and saying it is would be a lie in the customer's face.
      */}
      {imageSrc && <AiImageNote label="AI-generated" className="mb-1.5" />}

      {/* Add / stepper */}
      <AddButton product={product} className="mb-2" />

      {/* Name */}
      <Link
        to={to}
        className="line-clamp-2 min-h-9 text-[13px] font-medium leading-tight text-ink hover:text-primary-600"
      >
        {product.name}
      </Link>

      {/* Brand line */}
      {product.brand && (
        <div className="mt-1 flex items-center gap-2">
          <span className="truncate text-[11px] text-muted">{product.brand}</span>
          <span className="h-px flex-1 bg-line" />
        </div>
      )}

      {/*
        Price + MRP + size. The size used to float over the photo as a white
        chip, where pale packaging swallowed it and 11px made it the smallest
        thing on the card. Price and size answer one question together — "is
        this worth it?" — so they share a line. `truncate` on the size means a
        long one shortens itself rather than pushing the price out of view.
      */}
      <div className="mt-1 flex items-baseline gap-1.5">
        <span className="text-[15px] font-bold text-ink">{formatCurrency(product.price)}</span>
        {hasDiscount && (
          <span className="text-xs text-muted line-through">{formatCurrency(product.mrp)}</span>
        )}
        {sizeLabel && <span className="truncate text-xs text-muted">{sizeLabel}</span>}
      </div>

      {hasDiscount && (
        <span className="mt-0.5 text-[11px] font-semibold text-success">
          {discount}% OFF ON MRP
        </span>
      )}
    </div>
  );
}

export default ProductCard;
